import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import {
  useDeactivateTechnician,
  useActivateTechnician,
} from '@/hooks/useTechnicians'
import { getApiErrorMessage } from '@/services/api'
import type { Technician } from '@/types'

interface Props {
  technician: Technician | null
  // 'deactivate' shows the impact warning + confirm; 'activate' reactivates.
  action: 'deactivate' | 'activate'
  // Pre-deactivation impact check (2.10.7-003): count of future assigned jobs.
  futureJobsCount?: number
  onClose: () => void
  onDone?: () => void
}

const outlineBtnClass =
  'rounded-lg border-[#767676] px-3 py-3 text-base font-normal'

// 2.10.7 — Deactivate / Activate technician with a pre-deactivation impact check.
// Shell matches ToggleClientStatusDialog / DeleteClientDialog (Figma).
export function DeactivateTechnicianDialog({
  technician,
  action,
  futureJobsCount = 0,
  onClose,
  onDone,
}: Props) {
  const { t } = useTranslation(['technicians', 'common'])
  const deactivate = useDeactivateTechnician()
  const activate = useActivateTechnician()
  const pending = deactivate.isPending || activate.isPending
  const isDeactivate = action === 'deactivate'

  const confirm = async () => {
    if (!technician) return
    try {
      if (action === 'deactivate') {
        await deactivate.mutateAsync(technician.id)
        toast({ variant: 'success', title: t('deactivateDialog.deactivated') })
      } else {
        await activate.mutateAsync(technician.id)
        toast({ variant: 'success', title: t('deactivateDialog.activated') })
      }
      onDone?.()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          action === 'deactivate'
            ? t('deactivateDialog.deactivateError')
            : t('deactivateDialog.activateError')
        ),
      })
    }
  }

  return (
    <Dialog open={!!technician} onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-w-[600px] gap-0 border-border p-8 shadow-lg sm:rounded-lg [&>button]:right-[7px] [&>button]:top-[7px] [&>button]:rounded-full [&>button]:p-2'>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-3 pr-8'>
            <DialogTitle className='text-2xl font-semibold leading-[1.2] tracking-[-0.48px]'>
              {isDeactivate
                ? t('deactivateDialog.deactivateTitle')
                : t('deactivateDialog.activateTitle')}
            </DialogTitle>
            {isDeactivate && futureJobsCount > 0 && (
              <div className='rounded-lg border border-warning/30 bg-warning/10 p-3 text-base leading-[1.4] text-foreground'>
                {t('deactivateDialog.futureJobsWarning', {
                  count: futureJobsCount,
                })}
              </div>
            )}
            <p className='text-base leading-[1.4] text-foreground'>
              {isDeactivate
                ? t('deactivateDialog.confirmDeactivate')
                : t('deactivateDialog.reactivateQuestion')}
            </p>
          </div>

          <div className='flex items-center justify-end gap-4'>
            <Button
              variant='outline'
              className={outlineBtnClass}
              onClick={onClose}
              disabled={pending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              className={cn(
                'rounded-lg px-3 py-3 text-base font-normal text-white',
                isDeactivate
                  ? 'border border-[#ec221f] bg-[#ec221f] hover:bg-[#ec221f]/90'
                  : 'hover:bg-primary/90'
              )}
              disabled={pending}
              onClick={confirm}
            >
              {isDeactivate
                ? t('deactivateDialog.confirmDeactivation')
                : t('deactivateDialog.activate')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DeactivateTechnicianDialog
